import { GraphQLError } from "graphql"

import { CommerceService } from "@/services/commerce"

const CUSTOMER_FILTER_GROUP_PREDICATE = `key in ("adm_user", "adm_regionallead", "adm_admin", "idm_user", "idm_disponent", "idm_lead", "idm_theke", "idm_procurement", "adm_gvl")`
const CUSTOMER_FILTER_BRANCH_PREDICATE = `key in ("branch-00", "branch-06", "branch-07", "branch-08", "branch-26", "branch-36", "branch-66", "branch-76", "branch-86")`
const FALLBACK_CUSTOMER_FILTER_BRANCH_PREDICATE = `key in ("branch-00", "branch-06", "branch-07", "branch-08", "branch-26", "branch-36","branch-66", "branch-76", "branch-86")`
const BRANCH_06_FILTER_PREDICATE = `key in ("branch-06")`

const commerceApi = CommerceService.api()

/**
 * Gets list of customer groups that should be filtered out when querying customers
 *
 * @returns {Promise<String>}
 * @throws {Error}
 */
export const getExcludedCustomerGroups = async () => {
  const customerGroups = (
    await commerceApi
      .customerGroups()
      .get({
        queryArgs: { where: CUSTOMER_FILTER_GROUP_PREDICATE },
      })
      .execute()
  )?.body?.results

  if (!customerGroups || customerGroups.length === 0) {
    throw new Error("Failed to fetch customer groups")
  }

  return customerGroups.map((group) => `"${group.id}"`).join(",")
}

/**
 * Gets list of customer branches that should be excluded when querying customers
 *
 * @returns {Promise<String>}
 */
export const getExcludedIndustryKeys = async () => {
  return getJoinedContainerIds(CUSTOMER_FILTER_BRANCH_PREDICATE)
}

/**
 * Gets list of customer branches that should be excluded when querying customers
 *
 * @returns {Promise<String>}
 */
export const getFallbackExcludedIndustryKeys = async () => {
  return getJoinedContainerIds(FALLBACK_CUSTOMER_FILTER_BRANCH_PREDICATE)
}

/**
 * Retrieves the IDs of customer containers based on a predicate
 *
 * @param {string} predicate
 * @returns {Promise<String>}
 * @throws {Error}
 */
const getJoinedContainerIds = async (predicate) => {
  const customerContainers = (
    await commerceApi
      .customObjects()
      .withContainer({ container: "branchContainer" })
      .get({
        queryArgs: {
          where: predicate,
        },
      })
      .execute()
  )?.body?.results

  if (!customerContainers || customerContainers.length === 0) {
    throw new Error("Failed to fetch customer groups")
  }

  return customerContainers.map((container) => `"${container.id}"`).join(",")
}

/**
 * @param {int} page
 * @param {int} limit
 * @returns
 */
export const noDataReturn = (page, limit) => {
  return {
    items: [],
    limit,
    count: 0,
    total: 0,
    page,
  }
}

/**
 * Checks if a customers is internal based on their ID.
 *
 * @param {{id: ?string, customerNumber: ?string}}
 * @returns {Promise<boolean>}
 */
export const checkIfCustomerIsInternal = async ({ id, customerNumber }) => {
  const commerceApi = CommerceService.api()
  const excludedIndustryKeys = await getExcludedIndustryKeys()
  const identifier = id ? `id="${id}"` : `customerNumber="${customerNumber}"`

  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: {
          where: `${identifier} and custom(fields(industryKey(id is not defined or id in (${excludedIndustryKeys}))))`,
        },
      })
      .execute()
  )?.body?.results

  if (!customers || customers?.length === 0) {
    return false
  }

  return true
}

/**
 * Checks if a customer belongs to industry branch 06.
 *
 * @param {{customerNumber: ?string}}
 * @returns {Promise<boolean>}
 */
export const checkIfCustomerIsBranch06 = async ({ customerNumber }) => {
  const commerceApi = CommerceService.api()
  const branch06IndustryKeys = await getJoinedContainerIds(BRANCH_06_FILTER_PREDICATE)

  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: {
          where: `customerNumber="${customerNumber}" and custom(fields(industryKey(id is defined and id in (${branch06IndustryKeys}))))`,
        },
      })
      .execute()
  )?.body?.results

  if (!customers || customers?.length === 0) {
    return false
  }

  return true
}

/**
 *
 * @param {{id: ?string, customerNumber: ?string}}
 * @void
 * @throws {GraphQLError}
 */
export const internalCustomerException = async ({ id, customerNumber }) => {
  const isInternal = await checkIfCustomerIsInternal({ id, customerNumber })

  if (isInternal) {
    throw new GraphQLError("INTERNAL_CUSTOMER", {
      extensions: {
        code: "INTERNAL_CUSTOMER",
      },
    })
  }
}
